'use client';

import { __assign } from '../_virtual/_tslib.js';
import React__default, { forwardRef, useState, useRef, useCallback, useMemo, useImperativeHandle, useEffect } from 'react';
import MultipleRadio from '../MultipleRadio/index.js';
import { Wrapper, LabelWrapper, TextDanger, Container, CustomValue, Flag, CustomOptionItem, Text, InputWrapper, Input, RemoveIcon, ErrorMsg } from './I11lPhoneNumberInput.css.js';
import { VN_DIALCODE, t, IMG_URL, renderErrorMsg, enterOnlyNumber, I11L_LIMIT_NUMBER } from './helpers.js';
import useElementSize from '../hooks/useElementSize.js';
import useFetch from '../hooks/useFetch.js';
import 'react-dom';
import '../hooks/useMakeSearchable/types.js';
import { ELanguage } from '../Utils/constants.js';
import '../services/resumes/types.js';
import { isPhone } from '../Utils/responsiveHelper.js';

var COUNTRY_SPRITE_SHEET = IMG_URL + '/country-spritesheet.png?v=1';
var I11lPhoneNumberInput = forwardRef(function (_a, ref) {
    var _b = _a.label, label = _b === void 0 ? '' : _b, _c = _a.isRequired, isRequired = _c === void 0 ? false : _c, onChange = _a.onChange, _d = _a.className, className = _d === void 0 ? 'i11l-input-container' : _d, _e = _a.value, value = _e === void 0 ? '' : _e, _f = _a.language, language = _f === void 0 ? ELanguage.VI : _f, _g = _a.urlMeta, urlMeta = _g === void 0 ? 'https://ms.vietnamworks.com/meta/v1.0/international-phone-number' : _g, renderErrorElement = _a.renderErrorElement, _h = _a.hiddenDefaultError, hiddenDefaultError = _h === void 0 ? true : _h, _j = _a.hasError, hasError = _j === void 0 ? false : _j, _k = _a.dropdownWidth, dropdownWidth = _k === void 0 ? '' : _k, _l = _a.dropdownHeight, dropdownHeight = _l === void 0 ? '250px' : _l, _m = _a.enableClearInput, enableClearInput = _m === void 0 ? true : _m, _o = _a.hiddenInput, hiddenInput = _o === void 0 ? {
        id: '',
        className: '',
        name: ''
    } : _o, onFocus = _a.onFocus, onBlur = _a.onBlur, onPressEnter = _a.onPressEnter, placeholder = _a.placeholder, floatingConfig = _a.floatingConfig;
    var _p = useState([]), options = _p[0], setOptions = _p[1];
    var _q = useState(VN_DIALCODE), dialCode = _q[0], setDialCode = _q[1];
    var _r = useElementSize(), setRef = _r[0], size = _r[1];
    var _s = useState(value), inputValue = _s[0], setInputValue = _s[1];
    var _t = useState(value), hiddenValue = _t[0], setHiddenValue = _t[1];
    var _u = useState(false), isVietNam = _u[0], setIsVietNam = _u[1];
    var _v = useState(''), error = _v[0], setError = _v[1];
    var _w = useState(false), touched = _w[0], setTouched = _w[1];
    var _x = useState('none'), inputRemoveState = _x[0], setInputRemoveState = _x[1];
    var inputRef = useRef(null);
    var isSetInitialValue = useRef(false);
    var validate = function (phoneNumber) {
        var errorMsg = renderErrorMsg(language, phoneNumber, isVietNam);
        if (errorMsg === '') {
            setTouched(false);
        }
        if (!hiddenDefaultError) {
            setError(errorMsg);
        }
        return errorMsg;
    };
    var convertedPhoneNumber = useCallback(function (dialCode, value) {
        if (value === '')
            return '';
        return "".concat(dialCode, "-").concat(value);
    }, []);
    var setValue = function (target) {
        var _a;
        var value = target.value;
        value = value.replace(/\. /g, '');
        if (value.match(I11L_LIMIT_NUMBER)) {
            value = ((_a = value.match(I11L_LIMIT_NUMBER)) === null || _a === void 0 ? void 0 : _a[0]) || '';
            target.value = value;
        }
        setInputValue(value);
        // Handle validate phone number input if user touched
        if (touched) {
            validate(value);
        }
        // Convert data
        if (/^0/g.test(value) && isVietNam) {
            value = value.substring(1);
        }
        var convertedValue = convertedPhoneNumber(dialCode, value.trim());
        setHiddenValue(convertedValue);
        onChange && onChange(convertedValue);
    };
    var handleChange = function (e) {
        setValue(e.target);
        isSetInitialValue.current = true;
    };
    var handleChangeNation = useCallback(function (result) {
        isSetInitialValue.current = true;
        var value = inputValue;
        setDialCode(result.value);
        if (result.value === VN_DIALCODE && /^0/g.test(value)) {
            // Convert data
            value = value.substring(1);
        }
        var convertedValue = convertedPhoneNumber(result.value, value.trim());
        setHiddenValue(convertedValue);
        onChange && onChange(convertedValue);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [onChange, inputValue, convertedPhoneNumber]);
    var getValueSelected = useMemo(function () { return options.findIndex(function (item) { return item.dialCode === dialCode; }) + 1; }, [dialCode, options]);
    var handleClearInput = useCallback(function () {
        onChange && onChange('');
        if (inputRef.current) {
            inputRef.current.focus();
            setInputRemoveState('focus');
        }
        setInputValue('');
        setHiddenValue('');
    }, [onChange]);
    var handleInputFocus = function () {
        setInputRemoveState('focus');
        onFocus && onFocus();
    };
    useImperativeHandle(ref, function () { return ({
        validate: function () {
            return validate(inputValue);
        },
        errorMsg: function (phoneNumber) {
            var convertedPhoneNumber = phoneNumber.split('-');
            return validate(convertedPhoneNumber[1]);
        }
    }); });
    // Set initial value
    useEffect(function () {
        if (isSetInitialValue.current)
            return;
        if (value) {
            if (!value.match('-')) {
                var convertedValue = value.replace(/^0/, '');
                setDialCode(VN_DIALCODE);
                setInputValue(convertedValue);
                onChange && onChange(convertedPhoneNumber(VN_DIALCODE, convertedValue));
                setHiddenValue(convertedPhoneNumber(VN_DIALCODE, convertedValue));
                return;
            }
            var getValue = value.split('-');
            setDialCode(getValue[0]);
            setInputValue(getValue[1]);
            setHiddenValue(convertedPhoneNumber(getValue[0], getValue[1]));
        }
    }, [value, convertedPhoneNumber, onChange]);
    useEffect(function () {
        if (dialCode === '+84') {
            setIsVietNam(true);
            return;
        }
        setIsVietNam(false);
    }, [dialCode]);
    useEffect(function () {
        if (error !== '' && !touched)
            setTouched(true);
    }, [error, touched]);
    useFetch(urlMeta, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    }, function (data) {
        var convertedData = data.data.relationships.data.map(function (item) { return item.attributes; });
        setOptions(convertedData);
    });
    useEffect(function () {
        var setDialCodeFromUMD = function (e) {
            var _a = e.detail, dialCode = _a.dialCode, phoneNumber = _a.phoneNumber;
            setDialCode(dialCode);
            setInputValue(phoneNumber);
            setHiddenValue(convertedPhoneNumber(dialCode, phoneNumber));
        };
        if (typeof window === 'undefined')
            return;
        window.addEventListener('I11LPHONENUMBER', setDialCodeFromUMD);
        return function () {
            window.removeEventListener('I11LPHONENUMBER', setDialCodeFromUMD);
        };
    }, []);
    if (typeof window !== 'undefined') {
        window.I11lPhoneNumberInput = {
            validate: function () {
                return validate(inputValue);
            },
            errorMsg: function (phoneNumber) {
                var convertedPhoneNumber = phoneNumber.split('-');
                return validate(convertedPhoneNumber[1]);
            }
        };
    }
    var renderError = function (error) {
        return renderErrorElement ? renderErrorElement(error) : React__default.createElement(ErrorMsg, null, error);
    };
    var isError = error !== '';
    var renderDropdownWidth = size.width > 0 ? "".concat(size.width, "px") : '320px';
    var onKeyDown = function (e) {
        enterOnlyNumber(e);
        if (e.key === 'Enter') {
            onPressEnter && onPressEnter();
        }
    };
    return (React__default.createElement(Wrapper, null,
        label !== '' ? (React__default.createElement(LabelWrapper, null,
            isRequired ? React__default.createElement(TextDanger, null, "*") : null,
            " ",
            React__default.createElement("label", null, label))) : null,
        React__default.createElement(Container, { ref: setRef, className: "".concat(className, " ").concat(hasError ? 'error' : '', " ").concat(isError ? 'error' : '') },
            React__default.createElement(MultipleRadio, { name: 'i11l-multiple-radio', width: 'fit-content', value: getValueSelected, options: options, fieldValue: 'dialCode', fieldLabel: 'countryName', onChange: handleChangeNation, onBlur: onBlur, customValue: function (value) {
                    var _a = value, dialCode = _a.dialCode, iconPosition = _a.iconPosition;
                    return (React__default.createElement(CustomValue, { className: 'i11l-flag-selected' },
                        React__default.createElement(Flag, { url: COUNTRY_SPRITE_SHEET, x: iconPosition.x }),
                        dialCode));
                }, arrowDownSize: { width: 16, height: 16 }, spacingElement: 2, isMobile: isPhone(), dropdown: {
                    width: dropdownWidth !== '' ? dropdownWidth : renderDropdownWidth,
                    isSearchable: true,
                    searchBoxPlaceholder: t('Type country name or code', language),
                    searchFields: ['countryName', 'dialCode'],
                    maxHeight: dropdownHeight,
                    emptyText: t('No result found', language),
                    floatingConfig: __assign({}, floatingConfig),
                    customOptionItem: function (value) {
                        var countryName = value.countryName, dialCode = value.dialCode, iconPosition = value.iconPosition, active = value.active;
                        return (React__default.createElement(CustomOptionItem, null,
                            React__default.createElement(Flag, { url: COUNTRY_SPRITE_SHEET, x: iconPosition.x }),
                            React__default.createElement(Text, { className: 'i11l-value', active: active }, "".concat(countryName, " ").concat(dialCode))));
                    }
                } }),
            React__default.createElement(InputWrapper, null,
                React__default.createElement(Input, { type: 'tel', placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : '', value: inputValue, onChange: handleChange, onKeyDown: onKeyDown, className: 'i11l-input', onFocus: handleInputFocus, onMouseEnter: function () { return inputRemoveState !== 'focus' && setInputRemoveState('hover'); }, onMouseLeave: function () { return inputRemoveState !== 'focus' && setInputRemoveState('none'); }, onBlur: function () {
                        setInputRemoveState('none');
                        onBlur && onBlur();
                    }, ref: inputRef }),
                enableClearInput && (React__default.createElement(RemoveIcon, { onMouseOver: function () { return inputRemoveState !== 'focus' && setInputRemoveState('hover'); }, onMouseLeave: function () { return inputRemoveState !== 'focus' && setInputRemoveState('none'); }, className: "i11l-remove-icon ".concat(inputRemoveState === 'none' ? 'hidden' : inputValue !== '' ? '' : 'hidden'), onClick: handleClearInput })))),
        error && !hiddenDefaultError && renderError(error),
        React__default.createElement("input", { name: hiddenInput.name, className: hiddenInput.className, id: hiddenInput.id, type: 'hidden', value: hiddenValue })));
});

export { I11lPhoneNumberInput as default };
//# sourceMappingURL=index.js.map
